/**
 * /portal/acceso-gratis | /portal/free-access
 *
 * Paso final del free-join: cuenta Clerk + perfil completo → revelar link grupo free.
 * Sin perfil → redirect a completar-perfil. Sin sesión → registro free.
 */

import { HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSEO } from "@/hooks/useSEO";
import { isClerkAdmin, isProfileComplete } from "@/lib/auth";
import { BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_TELEGRAM_URL } from "@/lib/brandConfig";
import {
  buildFreeJoinCompleteProfileUrl,
  buildFreeJoinRegistroUrl,
  clearFreeJoinFlowFlag,
  FREE_JOIN_ROUTE,
  isFreeJoinFlowActive,
  persistFreeJoinContext,
  readFreeJoinCountry,
  readFreeJoinDestination,
  readFreeJoinLanguage,
} from "@/lib/freeJoinFlow";
import { resolveMembershipFlowLanguage } from "@/lib/membershipFlowI18n";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { trackLinkClickPostHog } from "@/lib/posthogAnalytics";
import { usePremiumInviteFreeTelegramPublicUrl } from "@/hooks/usePremiumInviteFreeTelegramPublicUrl";
import { resolveFreeJoinTelegramUrlByCountry } from "@/lib/resolveFreeJoinTelegramUrl";
import { useUser } from "@clerk/clerk-react";
import {
  CheckCircle2,
  ExternalLink,
  Loader2,
  MessageCircle,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";

export default function FreeAccessReady() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const langFromPath = resolveMembershipFlowLanguage(params, pathname);
  const sessionLang = readFreeJoinLanguage();
  const lang = sessionLang?.startsWith("en")
    ? "en"
    : langFromPath;
  const isEn = lang === "en";
  const { user, isLoaded, isSignedIn } = useUser();
  const countryFromUrl = (params.get("country") || "").trim().toUpperCase().slice(0, 2);
  const country =
    (countryFromUrl && /^[A-Z]{2}$/.test(countryFromUrl) ? countryFromUrl : null) ||
    readFreeJoinCountry() ||
    undefined;

  // Keep session coherent if user landed with ?country=
  useEffect(() => {
    if (country || sessionLang) {
      persistFreeJoinContext({
        country: country || undefined,
        language: sessionLang || lang,
      });
    }
  }, [country, sessionLang, lang]);

  const { url: publicFreeUrl, publicConfig } = usePremiumInviteFreeTelegramPublicUrl(
    true,
    country,
  );

  const destination = useMemo(() => {
    const fromSession = readFreeJoinDestination();
    if (fromSession) return fromSession;
    const byCountry = resolveFreeJoinTelegramUrlByCountry(publicConfig, country);
    if (byCountry) return byCountry;
    if (publicFreeUrl) return publicFreeUrl;
    return BRAND_TELEGRAM_URL?.trim() || null;
  }, [publicConfig, publicFreeUrl, country]);

  const viewedRef = useRef(false);
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) return;
    if (!isProfileComplete(user)) return;
    if (viewedRef.current) return;
    viewedRef.current = true;
    const wasFlow = isFreeJoinFlowActive();
    mirrorFunnelEvent({
      event_name: "free_join_link_revealed",
      template: "FreeAccessReady",
      flow_name: "free_join_clerk_profile",
      landing_path: pathname,
      language: lang,
      customer_email: user.primaryEmailAddress?.emailAddress,
      props: {
        funnel_stage: "free_link_ready",
        plan_tier: "free",
        has_destination: Boolean(destination),
        free_join_flow: wasFlow,
        country: country || undefined,
      },
    });
    // Flow completed — clear active flag (keep destination for refresh)
    clearFreeJoinFlowFlag();
  }, [isLoaded, isSignedIn, user, destination, pathname, lang, country]);

  useSEO({
    title: isEn
      ? `Your free group — ${BRAND_PERSON_NAME}`
      : `Tu grupo free — ${BRAND_PERSON_NAME}`,
    description: isEn
      ? `Open your free community link with ${BRAND_PERSON_NAME}.`
      : `Abrí el link del grupo free de ${BRAND_PERSON_NAME}.`,
    keywords: "",
    ogImage: `${BRAND_SITE_URL}/og.png`,
    canonical: `${BRAND_SITE_URL}${isEn ? FREE_JOIN_ROUTE.EN : FREE_JOIN_ROUTE.ES}`,
    robots: "noindex, nofollow" as const,
  });

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!isSignedIn || !user) {
    return (
      <Navigate
        to={buildFreeJoinRegistroUrl({ language: lang, country })}
        replace
      />
    );
  }

  if (isClerkAdmin(user)) {
    return <Navigate to="/admin" replace />;
  }

  if (!isProfileComplete(user)) {
    return (
      <Navigate
        to={buildFreeJoinCompleteProfileUrl({ language: lang, country })}
        replace
      />
    );
  }

  const displayName =
    (user.unsafeMetadata?.alias as string | undefined)?.trim() ||
    user.firstName ||
    user.primaryEmailAddress?.emailAddress?.split("@")[0] ||
    (isEn ? "there" : "vos");

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4 py-8 text-zinc-100">
      <Card className="w-full max-w-md border-white/10 bg-zinc-900/80 shadow-2xl shadow-black/40 backdrop-blur">
        <CardHeader className="space-y-3 pb-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl ring-2 ring-white/10">
            <img
              src={HEADER_PROFILE_IMAGE_URL}
              alt=""
              className="h-full w-full object-cover"
              width={64}
              height={64}
            />
          </div>
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/20">
            <CheckCircle2 className="h-6 w-6 text-emerald-300" />
          </div>
          <CardTitle className="flex items-center justify-center gap-2 text-xl text-zinc-100 sm:text-2xl">
            <Sparkles className="h-5 w-5 text-amber-300" />
            {isEn ? `You're in, ${displayName}!` : `¡Listo, ${displayName}!`}
          </CardTitle>
          <CardDescription className="text-sm text-zinc-400">
            {isEn
              ? `Profile complete. Here's your free group link with ${BRAND_PERSON_NAME}.`
              : `Perfil completo. Acá está el link del grupo free de ${BRAND_PERSON_NAME}.`}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 pt-2">
          {destination ? (
            <>
              <div className="rounded-xl border border-[#0088cc]/30 bg-[#0088cc]/10 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-[#0088cc]" />
                  <span className="font-semibold text-zinc-100">
                    {isEn ? "Free Telegram group" : "Grupo free de Telegram"}
                  </span>
                </div>
                <p className="text-xs leading-relaxed text-zinc-400">
                  {isEn
                    ? "This is the free community — not paid VIP. Open with the button below."
                    : "Esto es la comunidad free — no el VIP de pago. Abrí con el botón de abajo."}
                </p>
              </div>

              <Button
                asChild
                className="h-12 w-full gap-2 text-base font-semibold text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #2AABEE 0%, #229ED9 55%, #1B8BC4 100%)",
                }}
              >
                <a
                  href={destination}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackLinkClickPostHog("free_access_ready_join_group", destination);
                    mirrorFunnelEvent({
                      event_name: "free_join_telegram_open",
                      template: "FreeAccessReady",
                      flow_name: "free_join_clerk_profile",
                      landing_path: pathname,
                      language: lang,
                      props: {
                        plan_tier: "free",
                        funnel_stage: "free_link_click",
                      },
                    });
                  }}
                >
                  <MessageCircle className="h-5 w-5" />
                  {isEn ? "Open free group" : "Abrir grupo free"}
                  <ExternalLink className="h-4 w-4 opacity-80" />
                </a>
              </Button>
            </>
          ) : (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100/90">
              {isEn
                ? "Your profile is ready. The free group link isn't configured yet — check back soon or contact support."
                : "Tu perfil está listo. El link del grupo free aún no está configurado — volvé en un rato o contactá soporte."}
            </div>
          )}

          <MembershipPortalHint />

          <p className="text-center text-xs text-zinc-500">
            <Link to="/" className="underline-offset-2 hover:text-zinc-300 hover:underline">
              {isEn ? "Back to home" : "Volver al inicio"}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
